import React, { useState, useEffect } from 'react';
import { Heart, MessageCircle, Bookmark, Eye, ShoppingCart, Play, Book, Volume2, Cuboid as Cube } from 'lucide-react';
import { Post, ArtCategory } from '../types';
import { useAuth } from '../context/AuthContext';
import FollowButton from './FollowButton';
import { adsDisplayService } from '../services/adsDisplayService';
import AdCard from './AdCard';
import { postsService } from '../services/postsService';
import { useLanguage } from '../context/LanguageContext';

interface MasonryCardProps {
  post: Post;
  onLike: (postId: string) => void;
  onSave: (postId: string) => void;
  onOpenDetail: (post: Post) => void;
}

const MasonryCard: React.FC<MasonryCardProps> = ({ post, onLike, onSave, onOpenDetail }) => {
  const { user } = useAuth();
  const { select, locale } = useLanguage();
  const ctaLabels: Record<string, { es: string; en: string }> = {
    learn_more: { es: 'Más info', en: 'Learn more' },
    shop_now: { es: 'Comprar', en: 'Shop now' },
    sign_up: { es: 'Registro', en: 'Sign up' },
    download: { es: 'Descargar', en: 'Download' },
    contact_us: { es: 'Contacto', en: 'Contact us' },
    book_now: { es: 'Reservar', en: 'Book now' },
  };

  const getCallToActionLabel = (cta: string) => select(ctaLabels[cta] || { es: 'Ver más', en: 'View more' });

  const categoryLabels: Record<ArtCategory, string> = {
    music: select({ es: 'Música', en: 'Music' }),
    writing: select({ es: 'Escritura', en: 'Writing' }),
    tattoo: select({ es: 'Tatuajes', en: 'Tattoos' }),
    digital_design: select({ es: 'Diseño Digital', en: 'Digital Design' }),
    fashion_design: select({ es: 'Diseño de Moda', en: 'Fashion Design' }),
    photography: select({ es: 'Fotografía', en: 'Photography' }),
    sculpture: select({ es: 'Escultura', en: 'Sculpture' }),
    painting: select({ es: 'Pintura', en: 'Painting' }),
    dance: select({ es: 'Danza', en: 'Dance' }),
    filmmaking: select({ es: 'Cine', en: 'Filmmaking' }),
    makeup: select({ es: 'Maquillaje', en: 'Makeup' }),
  };

  // Hooks declared BEFORE early return (Rules of Hooks)
  const [isLiked, setIsLiked] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [likesCount, setLikesCount] = useState(post.likes || post.likes_count || 0);

  useEffect(() => {
    // Reset state when post changes
    setLikesCount(post.likes || post.likes_count || 0);
    setIsLiked(false);
    setIsSaved(false);
    
    if (post.type === 'ad') return;
    
    checkIfLiked();
    checkIfSaved();
  }, [post.id, post.type]);

  const checkIfLiked = async () => {
    try {
      const liked = await postsService.checkIfLiked(post.id);
      setIsLiked(liked);
    } catch (err) {
      console.error('Error checking like status:', err);
    }
  };

  const checkIfSaved = async () => {
    try {
      const saved = await postsService.checkIfSaved(post.id);
      setIsSaved(saved);
    } catch (err) {
      console.error('Error checking save status:', err);
    }
  };

  // If this is an ad, use the simplified AdCard component
  if (post.type === 'ad') {
    return (
      <div className="flex items-center justify-center">
        <AdCard post={post} />
      </div>
    );
  }

  const handleAdClick = async () => {
    if (post.type === 'ad' && post.ad_id) {
      console.log('🖱️ Ad clicked in MasonryCard:', post.ad_id, post.title);
      // Track ad click
      await adsDisplayService.trackAdClick(post.ad_id, user?.id);
      console.log('✅ Ad click tracked in MasonryCard');
      
      // Open destination URL if available
      if (post.destination_url) {
        console.log('🌐 Opening destination URL from MasonryCard:', post.destination_url);
        window.open(post.destination_url, '_blank');
      } else {
        console.log('ℹ️ No destination URL for ad in MasonryCard');
      }
    }
  };

  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Don't allow liking ads
    if (post.type === 'ad') return;
    
    if (isLiked) {
      // Unlike
      setIsLiked(false);
      setLikesCount(prev => Math.max(0, prev - 1));
    } else {
      // Like
      setIsLiked(true);
      setLikesCount(prev => prev + 1);
    }
    
    onLike(post.id);
  };

  const handleSave = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Don't allow saving ads
    if (post.type === 'ad') return;
    
    setIsSaved(!isSaved);
    onSave(post.id);
  };

  const handleTagClick = (tag: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Don't navigate for ad tags
    if (post.type === 'ad') return;
    
    const event = new CustomEvent('navigate', { 
      detail: { 
        tab: 'explore', 
        searchQuery: tag 
      } 
    });
    window.dispatchEvent(event);
  };

  const navigateToProfile = (e: React.MouseEvent) => {
    e.stopPropagation();
    const event = new CustomEvent('navigate', { 
      detail: { 
        tab: 'profile', 
        userId: post.author.id 
      } 
    });
    window.dispatchEvent(event);
  };

  const formatPrice = (price: number) =>
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR',
    }).format(price);

  const getCardType = () => {
    if (!post.media || post.media.length === 0) return 'text';
    const primaryMedia = post.media[0];
    return primaryMedia.type;
  };

  const getCardHeight = () => {
    const type = getCardType();
    switch (type) {
      case 'image':
        return 'auto'; // Natural image height
      case 'video':
        return '300px';
      case 'audio':
        return '180px';
      case 'document':
        return '220px';
      case '3d':
        return '250px';
      default:
        return '200px';
    }
  };

  const renderMediaPreview = () => {
    const type = getCardType();
    const media = post.media?.[0];

    switch (type) {
      case 'image':
        return (
          <div className="relative group overflow-hidden">
            <img
              src={media?.url || media?.content_url}
              alt={post.title}
              className="w-full h-auto object-cover transition-transform duration-300 group-hover:scale-105 protected-media"
              style={{ maxHeight: '400px' }}
              onContextMenu={(e) => e.preventDefault()}
              onDragStart={(e) => e.preventDefault()}
              draggable={false}
              onError={(e) => {
                console.error('Error loading image:', media?.url);
                e.currentTarget.src = 'https://images.pexels.com/photos/1183992/pexels-photo-1183992.jpeg?auto=compress&cs=tinysrgb&w=300';
              }}
            />
            <div className="watermark-overlay"></div>
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300" />
          </div>
        );

      case 'video':
        return (
          <div className="relative group overflow-hidden" style={{ height: getCardHeight() }}>
            <video
              src={media?.url || media?.content_url}
              muted
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 protected-media"
              onContextMenu={(e) => e.preventDefault()}
              onDragStart={(e) => e.preventDefault()}
              draggable={false}
              controlsList="nodownload nofullscreen noremoteplayback"
              disablePictureInPicture
              onError={() => {
                console.error('Error loading video:', media?.url);
              }}
            />
            <div className="watermark-overlay"></div>
            <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center group-hover:bg-opacity-50 transition-all duration-300">
              <div className="w-16 h-16 bg-white bg-opacity-90 rounded-full flex items-center justify-center">
                <Play size={24} className="text-gray-800 ml-1" />
              </div>
            </div>
            <div className="absolute bottom-3 right-3 bg-black bg-opacity-70 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1 z-20">
              <Play size={12} />
              {select({ es: 'Video', en: 'Video' })}
            </div>
          </div>
        );

      case 'audio':
        return (
          <div 
            className="relative group overflow-hidden bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 flex items-center justify-center"
            style={{ height: getCardHeight() }}
          >
              <div className="text-center text-white p-6">
                <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Volume2 size={32} />
                </div>
                <h3 className="font-bold text-lg mb-2">{post.title}</h3>
                <p className="text-white text-opacity-80 text-sm">
                  {select({ es: 'Por', en: 'By' })} {post.author.full_name}
                </p>
              </div>
              <div className="absolute bottom-3 right-3 bg-black bg-opacity-70 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1 z-20">
                <Volume2 size={12} />
              {select({ es: 'Audio', en: 'Audio' })}
              </div>
            </div>
          );

      case 'document':
        return (
          <div 
            className="relative group overflow-hidden bg-gradient-to-br from-blue-500 to-indigo-600 flex flex-col justify-center items-center p-6"
            style={{ height: getCardHeight() }}
          >
            <div className="text-center text-white">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <Book size={32} />
              </div>
              <h3 className="font-bold text-lg mb-2 line-clamp-2">{post.title}</h3>
              <p className="text-white text-opacity-80 text-sm">{media?.file_name}</p>
              <p className="text-white text-opacity-60 text-xs mt-1">
                {((media?.file_size || 0) / 1024 / 1024).toFixed(1)} MB
              </p>
            </div>
            <div className="absolute bottom-3 right-3 bg-black bg-opacity-70 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1 z-20">
              <Book size={12} />
              {media?.file_name?.includes('.pdf')
                ? 'PDF'
                : media?.file_name?.includes('.epub')
                  ? 'EPUB'
                  : select({ es: 'Documento', en: 'Document' })}
            </div>
          </div>
        );

      case '3d':
        return (
          <div 
            className="relative group overflow-hidden bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center"
            style={{ height: getCardHeight() }}
          >
            <div className="text-center text-white p-6">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:rotate-12 transition-transform duration-300">
                <Cube size={32} />
              </div>
              <h3 className="font-bold text-lg mb-2 line-clamp-2">{post.title}</h3>
              <p className="text-white text-opacity-80 text-sm">
                {select({ es: 'Modelo 3D interactivo', en: 'Interactive 3D model' })}
              </p>
            </div>
            <div className="absolute bottom-3 right-3 bg-black bg-opacity-70 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1 z-20">
              <Cube size={12} />
              3D
            </div>
          </div>
        );

      default:
        return (
          <div 
            className="relative group overflow-hidden bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center"
            style={{ height: getCardHeight() }}
          >
            <div className="text-center text-white p-6">
              <div className="text-4xl mb-2">🎨</div>
              <h3 className="font-bold text-lg mb-2 line-clamp-2">{post.title}</h3>
              <p className="text-white text-opacity-80 text-sm">
                {select({ es: 'Arte conceptual', en: 'Conceptual art' })}
              </p>
            </div>
          </div>
        );
    }
  };

  return (
    <div 
      className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer group transform hover:-translate-y-1 select-none"
      onClick={() => {
        if (post.type === 'ad') {
          handleAdClick();
        } else {
          onOpenDetail(post);
        }
      }}
      style={{ breakInside: 'avoid', marginBottom: '1rem' }}
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
    >
      {/* Sponsored/Ad Badge */}
      {(post.is_sponsored || post.type === 'ad') && (
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold flex items-center gap-2">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
              {post.type === 'ad'
                ? select({ es: 'PUBLICIDAD', en: 'AD' })
                : select({ es: 'PROMOCIONADO', en: 'SPONSORED' })}
            </span>
            {post.type === 'ad' && post.call_to_action && (
              <span className="text-xs bg-white text-blue-600 px-2 py-1 rounded-full font-bold">
                {getCallToActionLabel(post.call_to_action)}
              </span>
            )}
          </div>
        </div>
      )}
      
      {/* Media Preview */}
      {renderMediaPreview()}

      {/* Sale Badge */}
      {post.for_sale && (
        <div className="absolute top-3 right-3 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 shadow-lg z-10">
          <ShoppingCart size={12} />
          {formatPrice(post.price!)}
        </div>
      )}

      {/* Content */}
      <div className="p-4">
        {/* Author */}
        <div className="flex items-center gap-3 mb-3">
          <div 
            className="cursor-pointer hover:scale-105 transition-transform"
            onClick={navigateToProfile}
          >
            <img
              src={post.author.avatar_url || 'https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=32&h=32&fit=crop'}
              alt={post.author.full_name}
              className="w-8 h-8 rounded-full object-cover"
            />
          </div>
          <div 
            className="flex-1 cursor-pointer hover:text-purple-600 transition-colors"
            onClick={navigateToProfile}
          >
            <h3 className="font-semibold text-sm text-gray-900">{post.author.full_name}</h3>
            <p className="text-xs text-gray-500">@{post.author.username}</p>
          </div>
          <FollowButton 
            userId={post.author.id} 
            username={post.author.username}
            size="sm"
            variant="outline"
            showIcon={false}
          />
        </div>

        {/* Title and Description */}
        <h2 className="font-bold text-gray-900 mb-2 line-clamp-2">{post.title}</h2>
        <p className="text-gray-700 text-sm mb-3 line-clamp-3 leading-relaxed">{post.description}</p>
        
        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {post.tags.slice(0, 3).map((tag, index) => (
              <button
                key={index}
                onClick={(e) => handleTagClick(tag, e)}
                className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full hover:bg-purple-100 hover:text-purple-700 transition-all duration-200 transform hover:scale-105"
              >
                #{tag}
              </button>
            ))}
            {post.tags.length > 3 && (
              <span className="px-2 py-1 bg-gray-50 text-gray-400 text-xs rounded-full">
                +{post.tags.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {post.type !== 'ad' ? (
              <button
                onClick={handleLike}
                className={`flex items-center gap-1 transition-colors ${
                  isLiked ? 'text-red-500' : 'text-gray-500 hover:text-red-500'
                }`}
              >
                <Heart size={16} fill={isLiked ? 'currentColor' : 'none'} />
                <span className="text-xs font-medium">{likesCount}</span>
              </button>
            ) : (
              <div className="flex items-center gap-1 text-gray-400">
                <Heart size={16} />
                <span className="text-xs">—</span>
              </div>
            )}
            
            {post.type !== 'ad' ? (
              <button className="flex items-center gap-1 text-gray-500 hover:text-blue-500 transition-colors">
                <MessageCircle size={16} />
                <span className="text-xs font-medium">{post.comments?.length || post.comments_count || 0}</span>
              </button>
            ) : (
              <div className="flex items-center gap-1 text-gray-400">
                <MessageCircle size={16} />
                <span className="text-xs">—</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 text-gray-500">
              <Eye size={14} />
              <span className="text-xs">{post.views || post.views_count || 0}</span>
            </div>
            
            {post.type !== 'ad' ? (
              <button
                onClick={handleSave}
                className={`transition-colors ${
                  isSaved ? 'text-purple-500' : 'text-gray-500 hover:text-purple-500'
                }`}
              >
                <Bookmark size={16} fill={isSaved ? 'currentColor' : 'none'} />
              </button>
            ) : (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleAdClick();
                }}
                className="text-blue-600 hover:text-blue-700 transition-colors"
                title={select({ es: 'Ver anuncio', en: 'View ad' })}
              >
                <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-xs font-bold">AD</span>
                </div>
              </button>
            )}
          </div>
        </div>
        
        {/* Category and Premium */}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
          <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
            {categoryLabels[post.category as ArtCategory] || post.category}
          </span>
          {(post.author.is_premium || post.is_premium) && (
            <span className="px-2 py-1 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs font-medium rounded-full">
              {select({ es: '✨ Premium', en: '✨ Premium' })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default MasonryCard;
